import type { QuickAnswerKind } from '@numeo/types'
import { Alert, Button, Chip, Spinner } from '@/components/ds2'
import { useGeneralAgentChatStore } from '../hooks/generalAgentChatStore'
import { useThreadScroll } from '../hooks/useThreadScroll'
import { INVOICING_SUGGESTIONS } from '../constants'
import { GeneralAgentComposer } from './GeneralAgentComposer'
import { GeneralAgentMessageList } from './GeneralAgentMessageList'
import { ToolQuestionThread } from './approval/ToolQuestionThread'
import { useCardResultsStore } from './results/cardResultsStore'

/** The empty chat's first screen (v17 `.chatwelcome`): the mark, the question and what the agent does. */
const EmptyChat = () => (
    <div className="flex flex-col items-center pb-[50px] pt-[65px] text-center sm:pb-[75px] sm:pt-[90px]">
        <span aria-hidden className="mb-5 text-[32px] leading-none text-accent">
            ✦
        </span>
        <h2 className="text-[21px] font-medium tracking-[-0.5px] text-foreground sm:text-2xl">How can I help with invoicing?</h2>
        <p className="mt-[13px] text-[13px] leading-relaxed text-foreground-muted">
            Check delivered loads, find missing documents,
            <br />
            and create invoices.
        </p>
    </div>
)

/** The invoicing chat's column: the thread, the three suggestions (empty chat only) and the composer, centred in the page's one panel. */
export const GeneralAgentChat = () => {
    const messages = useGeneralAgentChatStore((s) => s.messages)
    const state = useGeneralAgentChatStore((s) => s.state)
    const notes = useGeneralAgentChatStore((s) => s.notes)
    const storeSend = useGeneralAgentChatStore((s) => s.send)
    const quickAnswer = useGeneralAgentChatStore((s) => s.quickAnswer)
    const tabId = useGeneralAgentChatStore((s) => s.tabId)
    const pendingQuestions = useGeneralAgentChatStore((s) => s.pendingQuestions)
    const heldNotices = useGeneralAgentChatStore((s) => s.heldNotices)
    const retry = useGeneralAgentChatStore((s) => s.retry)
    // An interrupted turn shows its error where "Thinking…" was, with Retry; other errors keep the alert below.
    const interrupted = state.status === 'error' && state.resend !== undefined
    // A chat with nothing in it yet keeps the welcome, the suggestions and the composer together under the top bar (v17); once it holds a
    // message, the thread fills the panel and the composer sits at its foot.
    const fresh = messages.length === 0 && notes.length === 0
    // The card results are read after the turn's messages and drawn between them, so they move the bottom too.
    const cards = useCardResultsStore((s) => (s.tabId === tabId ? s.cards : null))

    // A new message is brought to the top of the view with its answer under it; otherwise the view follows the bottom (see the hook).
    const { scroller, onScroll } = useThreadScroll(tabId, messages, [state.status, pendingQuestions, cards, heldNotices.length])

    // A suggestion is answered without the model; while a turn runs it waits its turn like the composer does.
    const suggest = (kind: QuickAnswerKind, label: string) => {
        if (state.status !== 'waiting') void quickAnswer(kind, label)
    }

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div ref={scroller} onScroll={onScroll} className={`flex flex-col overflow-y-auto ${fresh ? 'flex-none' : 'min-h-0 flex-1'}`}>
                <div className={`mx-auto flex w-full max-w-[960px] flex-col gap-4 px-6 pt-4 ${fresh ? '' : 'flex-1'}`}>
                    {fresh && !interrupted && <EmptyChat />}
                    <GeneralAgentMessageList messages={messages} waiting={state.status === 'waiting'} />
                    <ToolQuestionThread />
                    {/* Under the card questions too: an answered question resumes the turn, so the agent's work comes after it. */}
                    {state.status === 'waiting' && (
                        <p role="status" className="flex items-center gap-2 self-start text-sm text-foreground-muted">
                            <Spinner size={14} /> Thinking…
                        </p>
                    )}
                    {state.status === 'error' && interrupted && (
                        <div role="alert" className="flex flex-wrap items-center gap-3 self-start text-sm text-danger">
                            <span>{state.message}</span>
                            <Button size="sm" variant="secondary" onClick={() => void retry()}>
                                Retry
                            </Button>
                        </div>
                    )}
                    {heldNotices.length > 0 && (
                        <p role="status" className="self-start text-xs text-foreground-muted">
                            Tells the agent once nothing waits on you: {heldNotices.join(' · ')}
                        </p>
                    )}
                </div>
            </div>
            <div className="mx-auto flex w-full max-w-[960px] flex-col gap-3 px-6 pb-4 pt-2">
                {/* The suggestions start a chat; once it holds a message they would only push the composer down. */}
                {fresh && (
                    <ul aria-label="Suggested questions" className="flex flex-wrap justify-center gap-2">
                        {INVOICING_SUGGESTIONS.map(({ kind, label }) => (
                            <li key={kind}>
                                <Chip variant="secondary" size="md" label={label} onClick={() => suggest(kind, label)} />
                            </li>
                        ))}
                    </ul>
                )}
                {state.status === 'error' && !interrupted && <Alert variant="danger" title={state.message} />}
                <GeneralAgentComposer onSend={(text) => void storeSend(text)} />
                <p className="text-center text-xs text-foreground-muted">Review each invoice before you create it. Nothing is sent without your approval.</p>
            </div>
        </div>
    )
}
